/** Botón flotante de WhatsApp, fijo abajo a la derecha en todas las páginas públicas. No usa el
 *  verde genérico de WhatsApp a propósito (pedido explícito, "se ve feo flotando por ahí" con
 *  el ícono de siempre) — cada sitio usa sus propios colores de marca:
 *  - "cp" (Calistenia Progresiva, fondo oscuro): círculo cyan con el ícono en negro.
 *  - "kidstenia" (fondo claro): círculo partido a la mitad cyan/magenta (mismo estilo que el
 *    anillo de la foto del profesor) con el ícono en blanco.
 *  Cuenta el click en Firestore (ver src/lib/cta-analytics.ts) — nada bloqueante, si falla el
 *  conteo el link a WhatsApp igual funciona. */

import { useState } from "react";
import { trackCtaClick } from "@/lib/cta-analytics";

const CYAN = "#2CC6F4";
const MAGENTA = "#FF78D4";

function WhatsAppGlyph({ color, size }: { color: string; size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill={color}
        d="M16 3C9.1 3 3.5 8.6 3.5 15.5c0 2.4.7 4.7 1.9 6.7L3 29l7-1.9c1.9 1.1 4.1 1.6 6 1.6 6.9 0 12.5-5.6 12.5-12.5S22.9 3 16 3Zm0 22.7c-1.9 0-3.7-.5-5.3-1.5l-.4-.2-4.2 1.1 1.1-4.1-.2-.4a10.2 10.2 0 0 1-1.6-5.5C5.4 9.7 10.2 5 16 5s10.6 4.7 10.6 10.5S21.8 25.7 16 25.7Zm5.8-7.9c-.3-.2-1.9-.9-2.2-1s-.5-.2-.7.2-.8 1-1 1.2-.4.2-.7.1a8.7 8.7 0 0 1-2.6-1.6 9.6 9.6 0 0 1-1.8-2.2c-.2-.3 0-.5.1-.7l.5-.5c.2-.2.2-.3.3-.5s.1-.4 0-.5-.7-1.7-1-2.3-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4a3.5 3.5 0 0 0-1.1 2.6c0 1.5 1.1 3 1.3 3.2s2.2 3.4 5.3 4.6c.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.9-.8 2.1-1.5.3-.7.3-1.4.2-1.5-.1-.2-.3-.3-.6-.4Z"
      />
    </svg>
  );
}

export function FloatingWhatsApp({
  href, theme, trackKey, onWhatsappClick,
}: {
  /** URL completa de wa.me, ya armada (con el número y el mensaje precargado) — se recibe
   *  entera en vez de phone+message por separado porque cada sitio ya arma esta URL a su
   *  manera (ver contacto_channels en el sitio principal y kidstenia_footer acá). */
  href: string;
  theme: "cp" | "kidstenia";
  trackKey: string;
  /** Efecto adicional al hacer clic — hoy lo usan ambos sitios (CP y Kidstenia, desde que
   *  Kidstenia también entra al CRM de leads unificado) para el evento GA4 `click_whatsapp` y,
   *  en Kidstenia, además para registrar un lead genérico de "contacto directo" (ver
   *  registerDirectWhatsappLead en kidstenia-landing.tsx) — sin esto, este botón quedaba
   *  invisible para el CRM, solo un contador sin fecha en Estadísticas. Opcional porque el
   *  conteo de clics (trackCtaClick, arriba) ya corre siempre, con o sin esto. */
  onWhatsappClick?: () => void;
}) {
  const [hover, setHover] = useState(false);
  if (!href) return null;
  const bg = theme === "kidstenia" ? `conic-gradient(${CYAN} 0deg 180deg, ${MAGENTA} 180deg 360deg)` : CYAN;
  const glyphColor = theme === "kidstenia" ? "#FFFFFF" : "#0a0a0c";

  return (
    // <aside> (landmark real, no solo position:fixed): sin esto el link quedaba flotando
    // fuera de <header>/<main>/<footer>, sin ningún landmark que lo contenga — hallazgo real
    // de auditoría de accesibilidad ("All page content should be contained by landmarks").
    <aside aria-label="Contacto rápido por WhatsApp">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => { trackCtaClick(trackKey); onWhatsappClick?.(); }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label="Escríbenos por WhatsApp"
        style={{
          position: "fixed", bottom: 22, right: 22, zIndex: 60,
          width: 58, height: 58, borderRadius: "50%", background: bg,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: hover ? "0 8px 24px rgba(0,0,0,0.35)" : "0 4px 16px rgba(0,0,0,0.25)",
          transform: hover ? "scale(1.08)" : "scale(1)",
          transition: "transform .18s ease, box-shadow .18s ease",
          textDecoration: "none", cursor: "pointer",
        }}
      >
        <WhatsAppGlyph color={glyphColor} size={28} />
      </a>
    </aside>
  );
}
